import './index.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api-client.js';
import { useApiQuery } from '../../hooks.js';
import { number, date } from '../../format.js';
import { ThemeToggle } from '../../components/layout.js';
import { Button, DataNote, Loading, Pagination, Panel, RequestError, SelectField, TextField } from '../../components/ui.js';
import { DashboardLayout, useDashboardLayout, type DashboardSectionDefinition, type DashboardSectionLayout } from './dashboard-layout.js';
import type { DashboardActivity } from './collection-status.js';
import { adminSessionQuery, logoutAdminSession } from './session.js';
import { SuggestionMessage } from './suggestion-message.js';
import {
  Activity14Days, AnnualCoverage, CandidateProgressPanels, CollectionLogsPanel, CollectionPipeline,
  GovernmentApisPanel, InventoryInsights, OperationalMetrics, ParallelCandidateActivity,
  RecentExecutions, RetryQueuePanel, SiteAccessAnalytics,
} from './observability-panels.js';
import type { AccessSummary, NodeDashboardMetrics } from './observability-panels.js';

function CoverageLegend() {
  return <div className="coverage-legend" aria-label="Legenda de cobertura"><span><i className="coverage-dot complete" />100% completa</span><span><i className="coverage-dot high" />75–&lt;100% alta</span><span><i className="coverage-dot partial" />35–74% parcial</span><span><i className="coverage-dot low" />Abaixo de 35% baixa</span></div>;
}

interface StateData { candidates: number; assets: number; profiles: Array<{ source: string; total: number }>; official_records: number; collector: string; states: string[] }
interface ApurationSettings { mode: 'both' | 'database' | 'tse'; available_modes: string[]; locations: number; candidates: number; last_checked_at: string | null; last_changed_at: string | null; refresh_interval_seconds: number }
interface AgentSettings { level: number | null; label: string; batch_size: number; workers: number; requests_per_second: number; storage: string; updated_at: string | null; writes_enabled?: boolean }
interface AgentControlJob { id: string | number; kind: string; year: number | null; status: string }
interface AgentControl {
  paused: boolean;
  writes_enabled: boolean;
  priority: 'automatic' | 'manual';
  manual_priority: 'candidates_first' | 'remaining_first';
  manual_job_priority: string | number | null;
  retry_seconds: number;
  priority_jobs: AgentControlJob[];
  agent_runtime: { online: boolean; pid: number | null; age_seconds: number | null };
  job: AgentControlJob | null;
}

const agentLevels: Record<number, AgentSettings> = {
  1: { level: 1, label: 'Conservador', batch_size: 10, workers: 1, requests_per_second: 2, storage: 'database', updated_at: null },
  2: { level: 2, label: 'Padrão', batch_size: 25, workers: 1, requests_per_second: 4, storage: 'database', updated_at: null },
  3: { level: 3, label: 'Rápido', batch_size: 50, workers: 2, requests_per_second: 8, storage: 'database', updated_at: null },
  4: { level: 4, label: 'Intenso', batch_size: 100, workers: 4, requests_per_second: 12, storage: 'database', updated_at: null },
  5: { level: 5, label: 'Máximo', batch_size: 200, workers: 8, requests_per_second: 16, storage: 'database', updated_at: null },
};

function DashboardShell({ children }: { children: React.ReactNode }) {
  return <div className="dashboard-admin-shell">
    <header className="dashboard-admin-header">
      <Link className="dashboard-admin-brand" to="/" aria-label="Vitrine de Político, início">
        <span className="dashboard-admin-mark" aria-hidden="true">V</span>
        <span><strong>Vitrine de Político</strong><small>Área administrativa</small></span>
      </Link>
      <div className="dashboard-admin-header-actions"><span className="dashboard-restricted-badge">Acesso restrito</span><ThemeToggle /></div>
    </header>
    <main className="dashboard-admin-main">{children}</main>
  </div>;
}

function AdminLogin({ onSuccess }: { onSuccess: () => void }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setError(''); setPending(true);
    try { await api('/api/session', { method: 'POST', body: JSON.stringify({ token }) }); setToken(''); onSuccess(); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'Não foi possível autenticar.'); }
    finally { setPending(false); }
  };
  return <>
    <section className="dashboard-auth-intro">
      <p className="dashboard-auth-eyebrow">Acompanhamento em tempo real</p>
      <h1>Agente de coleta</h1>
      <p>Entre para acompanhar a atividade operacional e os dados persistidos pelo sistema.</p>
    </section>
    <Panel className="admin-login">
      <div className="dashboard-login-heading"><span className="dashboard-login-icon" aria-hidden="true">⌑</span><div><h2>Entrar no painel</h2><p>Acesso administrativo protegido.</p></div></div>
      <form onSubmit={submit}>
        <TextField label="Token administrativo" type="password" autoComplete="current-password" value={token} onValueChange={setToken} required />
        {error && <p className="notice error" role="alert">{error}</p>}
        <Button type="submit" variant="primary" disabled={pending}>{pending ? 'Entrando…' : 'Acessar painel'}</Button>
      </form>
      <p className="dashboard-login-note">A atividade e as preferências do painel são consultadas na base de dados do projeto.</p>
    </Panel>
  </>;
}

interface SuggestionInboxResponse {
  items: Array<{ id: number; name: string; city: string; comment: string; created_at: string }>;
  total: number;
  page: number;
  size: number;
}

interface DashboardAnnouncement {
  enabled: boolean;
  title: string;
  message: string;
  context: string;
  source_label: string;
  source_url: string;
  starts_at: string;
  duration_hours: number;
  ends_at: string;
  status: 'disabled' | 'scheduled' | 'expired' | 'active';
  updated_at: string | null;
  revision: string;
}

type AnnouncementDraft = Pick<DashboardAnnouncement, 'enabled' | 'title' | 'message' | 'context' | 'source_label' | 'source_url' | 'duration_hours'> & { starts_at: string };

function toDateTimeLocal(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function announcementWindowStatus(draft: AnnouncementDraft): DashboardAnnouncement['status'] {
  if (!draft.enabled) return 'disabled';
  const start = new Date(draft.starts_at).getTime();
  const end = start + draft.duration_hours * 60 * 60 * 1000;
  if (!Number.isFinite(start) || Date.now() < start) return 'scheduled';
  return Date.now() >= end ? 'expired' : 'active';
}

const announcementStatusLabels: Record<DashboardAnnouncement['status'], string> = {
  disabled: 'Desativado', scheduled: 'Agendado', expired: 'Encerrado', active: 'Em exibição',
};

function AnnouncementSettingsPanel() {
  const queryClient = useQueryClient();
  const query = useApiQuery<DashboardAnnouncement>(['dashboard-site-announcement'], '/api/dashboard/site-announcement', true, { staleTime: 30_000 });
  const [draft, setDraft] = useState<AnnouncementDraft | null>(null);
  const savedDraft = useRef('');
  const [notice, setNotice] = useState('');
  const save = useMutation({
    mutationFn: (value: AnnouncementDraft) => api<DashboardAnnouncement>('/api/dashboard/site-announcement', {
      method: 'PUT',
      body: JSON.stringify({ ...value, starts_at: new Date(value.starts_at).toISOString() }),
    }),
    onSuccess: async (result) => {
      queryClient.setQueryData(['dashboard-site-announcement'], result);
      setNotice('Aviso salvo. O site respeitará o período escolhido.');
      await queryClient.invalidateQueries({ queryKey: ['dashboard-site-announcement'] });
    },
  });

  useEffect(() => {
    const settings = query.data;
    if (!settings) return;
    const normalized: AnnouncementDraft = {
      enabled: settings.enabled, title: settings.title, message: settings.message,
      context: settings.context, source_label: settings.source_label, source_url: settings.source_url,
      starts_at: toDateTimeLocal(settings.starts_at), duration_hours: settings.duration_hours,
    };
    savedDraft.current = JSON.stringify(normalized);
    setDraft(normalized);
  }, [query.data]);

  if (query.isLoading) return <Panel className="dashboard-announcement-panel"><Loading label="Carregando configuração do popup…" /></Panel>;
  if (query.error instanceof Error) return <Panel className="dashboard-announcement-panel"><RequestError message={query.error.message} /></Panel>;
  if (!draft) return null;

  const previewStatus = announcementWindowStatus(draft);
  const previewEnd = draft.starts_at && Number.isFinite(new Date(draft.starts_at).getTime())
    ? new Date(new Date(draft.starts_at).getTime() + draft.duration_hours * 60 * 60 * 1000)
    : null;
  const formattedEnd = previewEnd && Number.isFinite(previewEnd.getTime())
    ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(previewEnd)
    : '—';
  const update = <K extends keyof AnnouncementDraft>(key: K, value: AnnouncementDraft[K]) => setDraft((current) => current ? { ...current, [key]: value } : current);

  return <Panel className="dashboard-announcement-panel">
    <div className="dashboard-announcement-heading">
      <div><p className="dashboard-auth-eyebrow">Comunicação pública</p><h2>Aviso de abertura do site</h2><p>Edite o conteúdo do popup e determine quando ele fica disponível.</p></div>
      <span className={`dashboard-announcement-status is-${previewStatus}`}>{announcementStatusLabels[previewStatus]}</span>
    </div>
    <div className="dashboard-announcement-grid">
      <form className="dashboard-announcement-form" data-update-dirty={save.isPending || JSON.stringify(draft) !== savedDraft.current} onSubmit={(event) => { event.preventDefault(); setNotice(''); save.mutate(draft); }}>
        <label className="dashboard-announcement-toggle"><input type="checkbox" checked={draft.enabled} onChange={(event) => update('enabled', event.target.checked)} /><span><strong>Exibir popup</strong><small>O botão flutuante aparece enquanto o aviso estiver válido.</small></span></label>
        <label>Título<input value={draft.title} onChange={(event) => update('title', event.target.value)} maxLength={160} required /></label>
        <label>Mensagem principal<textarea value={draft.message} onChange={(event) => update('message', event.target.value)} maxLength={1800} rows={4} required /></label>
        <label>Complemento <span>(opcional)</span><textarea value={draft.context} onChange={(event) => update('context', event.target.value)} maxLength={1200} rows={3} /></label>
        <div className="dashboard-announcement-source-fields">
          <label>Texto da fonte<input value={draft.source_label} onChange={(event) => update('source_label', event.target.value)} maxLength={100} placeholder="Fonte: veículo ou órgão" /></label>
          <label>Link da fonte<input type="url" inputMode="url" value={draft.source_url} onChange={(event) => update('source_url', event.target.value)} maxLength={2048} placeholder="https://…" /></label>
        </div>
        <div className="dashboard-announcement-time-fields">
          <label>Início<input type="datetime-local" value={draft.starts_at} onChange={(event) => update('starts_at', event.target.value)} required /><small>Use “Começar agora” para publicar imediatamente.</small></label>
          <label>Duração em horas<input type="number" min={1} max={720} step={1} value={draft.duration_hours} onChange={(event) => update('duration_hours', Number(event.target.value))} required /><small>De 1 hora a 30 dias.</small></label>
        </div>
        <div className="dashboard-announcement-actions">
          <Button type="button" onClick={() => update('starts_at', toDateTimeLocal(new Date().toISOString()))}>Começar agora</Button>
          <Button type="submit" variant="primary" disabled={save.isPending || !draft.starts_at}>{save.isPending ? 'Salvando…' : 'Salvar aviso'}</Button>
        </div>
        {save.error instanceof Error && <p className="notice error" role="alert">{save.error.message}</p>}
        {notice && <p className="dashboard-announcement-saved" role="status">{notice}</p>}
      </form>
      <aside className="dashboard-announcement-preview" aria-label="Prévia do aviso">
        <div className="dashboard-announcement-preview-heading"><span>Prévia</span><span className={`dashboard-announcement-status is-${previewStatus}`}>{announcementStatusLabels[previewStatus]}</span></div>
        <h3>{draft.title || 'Título do aviso'}</h3>
        <p>{draft.message || 'A mensagem principal aparecerá aqui.'}</p>
        {draft.context && <p className="dashboard-announcement-preview-context">{draft.context}</p>}
        {(draft.source_label || draft.source_url) && <span className="dashboard-announcement-preview-source">{draft.source_label || 'Fonte'}{draft.source_url && ' ↗'}</span>}
        <small>O popup ficará disponível até {formattedEnd}. Cada pessoa poderá fechá-lo e reabri-lo pelo botão flutuante.</small>
      </aside>
    </div>
  </Panel>;
}

function SuggestionsInboxPanel() {
  const [page, setPage] = useState(1);
  const suggestions = useApiQuery<SuggestionInboxResponse>(
    ['dashboard-suggestions', page], `/api/dashboard/suggestions?page=${page}&size=20`, true,
    { staleTime: 15_000, refetchInterval: 30_000, keepPreviousData: true },
  );
  if (suggestions.isLoading) return <Loading label="Carregando sugestões recebidas…" />;
  if (suggestions.error instanceof Error) return <RequestError message={suggestions.error.message} />;
  const data = suggestions.data;
  if (!data?.items.length) return <p className="suggestions-inbox-empty">Nenhuma sugestão recebida ainda.</p>;
  return <div className="suggestions-inbox">
    <div className="suggestions-inbox-toolbar"><span className="suggestions-inbox-count">{number(data.total)} {data.total === 1 ? 'sugestão recebida' : 'sugestões recebidas'}</span><span className="suggestions-inbox-refresh"><i aria-hidden="true" />Atualização automática a cada 30 s</span></div>
    <ol aria-label="Sugestões recebidas">
      {data.items.map((suggestion) => <SuggestionMessage key={suggestion.id} suggestion={suggestion} />)}
    </ol>
    <Pagination page={data.page} size={data.size} total={data.total} onChange={setPage} showJump={false} ariaLabel="Paginação de sugestões" />
  </div>;
}

export function DashboardPage() {
  const client = useQueryClient();
  const session = useQuery(adminSessionQuery);
  const [tab, setSelectedTab] = useState<'federal' | 'state'>('federal');
  const selectedTab = useApiQuery<{ tab: 'federal' | 'state' }>(['dashboard-tab'], '/api/dashboard/tab', Boolean(session.data?.authenticated));
  const state = useApiQuery<StateData>(['dashboard-state'], '/api/dashboard/state', Boolean(session.data?.authenticated && tab === 'state'));
  const control = useApiQuery<{ requested: boolean; configured: boolean; running: boolean; message: string }>(['state-agent-control'], '/api/state-agent-control', Boolean(session.data?.authenticated && tab === 'state'));
  const dashboardLayout = useDashboardLayout(Boolean(session.data?.authenticated));
  const persistTab = useMutation({ mutationFn: (next: 'federal' | 'state') => api('/api/dashboard/tab', { method: 'PUT', body: JSON.stringify({ tab: next }) }), onSuccess: (_result, next) => { setSelectedTab(next); void client.invalidateQueries({ queryKey: ['dashboard-tab'] }); } });
  const saveControl = useMutation({ mutationFn: (requested: boolean) => api('/api/state-agent-control', { method: 'POST', body: JSON.stringify({ requested }) }), onSuccess: () => { void client.invalidateQueries({ queryKey: ['state-agent-control'] }); } });
  const logout = useMutation({ mutationFn: () => logoutAdminSession(client) });

  useEffect(() => { if (selectedTab.data?.tab) setSelectedTab(selectedTab.data.tab); }, [selectedTab.data?.tab]);
  useEffect(() => {
    document.title = 'Dashboard administrativo | Vitrine de Político';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Área administrativa para acompanhar o agente de coleta e os registros do sistema.');
  }, []);
  useEffect(() => {
    if (!session.data?.authenticated) return;
    const timer = window.setInterval(() => { void client.invalidateQueries({ queryKey: ['dashboard-overview'] }); }, 15_000);
    return () => window.clearInterval(timer);
  }, [session.data?.authenticated, client]);

  if (session.isLoading) return <DashboardShell><Loading label="Verificando sessão administrativa…" /></DashboardShell>;
  if (!session.data?.authenticated) return <DashboardShell><AdminLogin onSuccess={() => { void client.invalidateQueries({ queryKey: ['session'] }); }} /></DashboardShell>;
  return <DashboardShell>
    <section className="dashboard-page-heading">
      <div><p className="dashboard-auth-eyebrow">Painel administrativo</p><h1>Acompanhamento em tempo real</h1><p>Acompanhe o estado da coleta e os registros disponíveis.</p></div>
      <Button type="button" onClick={() => logout.mutate()} disabled={logout.isPending}>{logout.isPending ? 'Encerrando…' : 'Encerrar sessão'}</Button>
    </section>
    <div className="dashboard-session-toolbar"><span><i aria-hidden="true" /> Sessão administrativa ativa</span><span>Preferências compartilhadas na base de dados</span></div>
    <AnnouncementSettingsPanel />
    <nav className="dashboard-tabs" aria-label="Painel de coleta" role="tablist">
      <Button variant="unstyled" type="button" role="tab" aria-selected={tab === 'federal'} className={tab === 'federal' ? 'active' : ''} onClick={() => persistTab.mutate('federal')}>Deputados federais e senadores</Button>
      <Button variant="unstyled" type="button" role="tab" aria-selected={tab === 'state'} className={tab === 'state' ? 'active' : ''} onClick={() => persistTab.mutate('state')}>Deputados estaduais</Button>
      {persistTab.error instanceof Error && <span className="notice error">{persistTab.error.message}</span>}
    </nav>
    {tab === 'federal' ? <FederalDashboard layout={dashboardLayout.layouts.federal} ready={dashboardLayout.ready} loadError={dashboardLayout.loadError} saveMessage={dashboardLayout.message} onLayoutChange={dashboardLayout.updateLayout} /> : state.isLoading ? <Loading label="Consultando os dados estaduais de Paraná e Santa Catarina…" /> : state.error ? <RequestError message={state.error.message} /> : state.data ? <StateDashboard data={state.data} control={control.data} onControl={(requested) => saveControl.mutate(requested)} layout={dashboardLayout.layouts.state} ready={dashboardLayout.ready} loadError={dashboardLayout.loadError} saveMessage={dashboardLayout.message} onLayoutChange={dashboardLayout.updateLayout} /> : null}
  </DashboardShell>;
}

function FederalDashboard({ layout, ready, loadError, saveMessage, onLayoutChange }: {
  layout: DashboardSectionLayout;
  ready: boolean;
  loadError: boolean;
  saveMessage: string;
  onLayoutChange: (tab: 'federal' | 'state', layout: DashboardSectionLayout) => void;
}) {
  const client = useQueryClient();
  const agentControl = useApiQuery<AgentControl>(['agent-control'], '/api/agent-control', true, { staleTime: 5_000, refetchInterval: 5_000 });
  const metrics = useApiQuery<NodeDashboardMetrics>(['node-dashboard-metrics'], '/api/dashboard/metrics', true, { staleTime: 10_000, refetchInterval: 15_000 });
  const nodeCollection = useApiQuery<{ writes_enabled: boolean }>(['node-collection'], '/api/node-collection', true, { staleTime: 5_000, refetchInterval: 15_000 });
  const releaseRetries = useMutation({
    mutationFn: () => api<{ released: number }>('/api/retries/release', { method: 'POST' }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['node-dashboard-metrics'] }); void client.invalidateQueries({ queryKey: ['node-collection'] }); void client.invalidateQueries({ queryKey: ['dashboard-operations'] }); },
  });
  const electionSourceEnabled = !layout.collapsed_sections.includes('federal-election-source');
  const electionSettings = useApiQuery<ApurationSettings>(['election-apuration-settings'], '/api/election-apuration-settings', electionSourceEnabled);
  const accesses = useApiQuery<AccessSummary>(['site-accesses'], '/api/accesses', true, { staleTime: 30_000, refetchInterval: 30_000 });
  const [collectionLogFilter, setCollectionLogFilter] = useState('');
  const [addApiRequest, setAddApiRequest] = useState(0);
  const controlState = agentControl.data?.paused ? 'PAUSADO'
    : agentControl.data?.job?.status === 'running' ? 'EM EXECUÇÃO'
      : agentControl.data?.job?.status === 'queued' ? 'NA FILA'
        : agentControl.data?.agent_runtime.online ? 'AGENTE ATIVO'
          : agentControl.error ? 'SEM CONEXÃO' : agentControl.isLoading ? 'CONSULTANDO…' : 'OCIOSO';
  const pipelineCandidate = metrics.data?.parallel_candidates[0];
  const pipelineStages: Record<string, string> = {
    directory: 'Cadastro', politician: 'Perfil', supplementary: 'Dados complementares',
    biography: 'Dados complementares', votes: 'Votações', cgu: 'Emendas',
  };
  const lastElectionCheck = electionSettings.data?.last_checked_at
    ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(electionSettings.data.last_checked_at))
    : '—';
  const accessUpdatedAt = accesses.dataUpdatedAt
    ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(accesses.dataUpdatedAt))
    : '—';
  const sections: DashboardSectionDefinition[] = [
    {
      id: 'federal-inventory',
      title: 'Dados cadastrados na base',
      description: 'Resumo do que já está gravado, separado da atividade atual do agente.',
      status: <span className="dashboard-section-meta">Inventário consultado às {metrics.data?.generated_at ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(metrics.data.generated_at)) : '—'} · atualização a cada 15 s</span>,
      content: <InventoryInsights metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-agent-controls', title: 'Controles do agente',
      description: 'Gerencie a execução sem encerrar consultas à força.',
      status: <span className={`agent-control-status${controlState === 'EM EXECUÇÃO' || controlState === 'AGENTE ATIVO' ? ' is-active' : controlState === 'PAUSADO' ? ' is-paused' : ''}`} aria-live="polite">{controlState}</span>,
      content: <AgentControlsPanel control={agentControl.data} loading={agentControl.isLoading} error={agentControl.error instanceof Error ? agentControl.error.message : ''} />,
    },
    {
      id: 'federal-processing-settings', title: 'Visão operacional',
      description: 'Velocidade e volume observados no job atual.',
      status: <OperationalHeaderActions metrics={metrics.data} loading={metrics.isLoading} />,
      content: <div className="dashboard-operations-panel"><OperationalMetrics metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} /><div className="dashboard-operations-rhythm"><div><h3>Força de processamento do agente</h3><p>Regule lotes, tarefas paralelas e consultas por segundo. A preferência escolhida orienta a coleta; as fontes oficiais ainda podem impor limites próprios.</p></div><AgentProcessingSettingsPanel /></div></div>,
    },
    {
      id: 'federal-candidate-progress', title: 'Candidatos prioritários do TSE · ano de dados 2026',
      description: '',
      status: <span className="dashboard-section-meta">TSE 2026 · dados 2026</span>,
      content: <CandidateProgressPanels metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-current-batch-progress', title: 'Lote 1 · candidatos prioritários do TSE',
      description: '',
      status: <span className="dashboard-section-meta">Lote 1 · dados 2026</span>,
      content: <CandidateProgressPanels batch metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-parallel-candidates', title: 'Atividade dos candidatos em paralelo',
      description: 'Progresso aproximado das etapas da coleta e últimos logs por candidato; não representa cobertura completa da base.',
      status: <span className="dashboard-section-meta">{number(metrics.data?.processing.concurrency ?? 0)} em processamento paralelo</span>,
      content: <ParallelCandidateActivity metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-pipeline', title: 'Etapa da coleta',
      description: pipelineCandidate?.name ?? 'Nenhum candidato em atividade',
      status: <span className="dashboard-section-meta">{pipelineCandidate?.phase ? pipelineStages[pipelineCandidate.phase] ?? 'Dados complementares' : 'Sem etapa atual'}</span>,
      content: <CollectionPipeline metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-coverage', title: 'Cobertura por ano',
      description: 'Consultas concluídas entre os políticos com mandato em cada ano. Perfil mostra o registro atual.',
      status: <CoverageLegend />,
      content: <AnnualCoverage metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-activity-14d', title: 'Atividade dos últimos 14 dias',
      description: 'Coberturas gravadas ou atualizadas por dia.',
      content: <Activity14Days metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-government-apis', title: 'APIs do governo',
      description: 'URLs dos conectores, credenciais, estado das consultas e erros recentes. APIs adicionais podem ser cadastradas e testadas; só fontes integradas ao coletor alimentam os dados.',
      status: <Button type="button" onClick={() => setAddApiRequest((request) => request + 1)}>Adicionar API</Button>,
      content: <GovernmentApisPanel addRequested={addApiRequest} />,
    },
    {
      id: 'federal-election-source', title: 'Apuração do 1º turno de 2026',
      description: 'Escolha de onde as consultas públicas obtêm os resultados eleitorais.',
      status: <span className="dashboard-section-meta">Última consulta TSE: {lastElectionCheck}</span>,
      content: <ElectionSourceSettingsPanel enabled={electionSourceEnabled} settings={electionSettings.data} loading={electionSettings.isLoading} error={electionSettings.error instanceof Error ? electionSettings.error : null} />,
    },
    {
      id: 'federal-activity-terminal', title: 'Terminal · atividade da busca',
      description: '',
      status: <span className={`dashboard-terminal-header-status${controlState === 'EM EXECUÇÃO' || controlState === 'AGENTE ATIVO' ? ' is-live' : ''}`}><strong>{controlState}</strong></span>,
      content: <DashboardActivityPanel metrics={metrics.data} />,
    },
    {
      id: 'federal-collection-logs', title: 'Log da coleta',
      description: 'Conectado · atualiza automaticamente e mantém os dados em caso de falha.',
      status: <DashboardSectionFilter value={collectionLogFilter} onChange={setCollectionLogFilter} />,
      content: <CollectionLogsPanel filter={collectionLogFilter} />,
    },
    {
      id: 'federal-retries', title: 'Fila de novas tentativas',
      description: 'Adiante as tentativas pendentes e acorde o agente para processá-las.',
      status: <div className="dashboard-retry-header"><p>{number(metrics.data?.retry_queue.pending ?? 0)} pendente(s)</p><Button type="button" disabled={nodeCollection.data?.writes_enabled !== true || !metrics.data?.retry_queue.pending || releaseRetries.isPending} onClick={() => releaseRetries.mutate()}>{releaseRetries.isPending ? 'Liberando…' : 'Liberar e processar pendentes'}</Button></div>,
      content: <RetryQueuePanel metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} writesEnabled={nodeCollection.data?.writes_enabled === true} releaseError={releaseRetries.error instanceof Error ? releaseRetries.error.message : ''} />,
    },
    {
      id: 'federal-recent-jobs', title: 'Execuções recentes',
      description: '',
      content: <RecentExecutions metrics={metrics.data} loading={metrics.isLoading} error={metrics.error instanceof Error ? metrics.error : null} />,
    },
    {
      id: 'federal-access-analytics', title: 'Visualizações agregadas do site',
      description: '',
      status: <span className="dashboard-section-meta">Atualizado às {accessUpdatedAt}</span>,
      content: <SiteAccessAnalytics data={accesses.data} loading={accesses.isLoading} error={accesses.error instanceof Error ? accesses.error : null} />,
    },
    {
      id: 'federal-suggestions', title: 'Sugestões recebidas',
      description: 'Mensagens enviadas pelo formulário público do rodapé.',
      content: <SuggestionsInboxPanel />,
    },
  ];
  return <section className="dashboard-content">
    <DashboardLayout tab="federal" sections={sections} layout={layout} ready={ready} loadError={loadError} saveMessage={saveMessage} onLayoutChange={onLayoutChange} />
    <DataNote>Esta página não executa migrations nem inicia processos locais. Os comandos atualizam a fila Node; o worker independente precisa estar ativo para processá-la.</DataNote>
  </section>;
}

function DashboardSectionFilter({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <label className="dashboard-log-filter">Filtrar logs<input type="search" value={value} onChange={(event) => onChange(event.target.value)} placeholder="Fonte, candidato ou erro" /></label>;
}

function AgentControlsPanel({ control, loading, error }: { control: AgentControl | undefined; loading: boolean; error: string }) {
  const client = useQueryClient();
  const [feedback, setFeedback] = useState('');
  type ControlChange =
    | { kind: 'execution'; action: 'start' | 'pause' | 'resume' }
    | { kind: 'backfill' }
    | { kind: 'recover' }
    | { kind: 'priority'; value: 'automatic' | 'manual' }
    | { kind: 'manual-priority'; value: 'candidates_first' | 'remaining_first' }
    | { kind: 'job-priority'; value: string }
    | { kind: 'retry'; value: number };
  const change = useMutation({
    mutationFn: async (request: ControlChange) => {
      if (request.kind === 'execution') {
        const result = await api<{ message: string }>('/api/agent-control', { method: 'POST', body: JSON.stringify({ action: request.action }) });
        return result.message;
      }
      if (request.kind === 'backfill') {
        const result = await api<{ job_id?: string | number; job?: { id?: string | number } }>('/api/data-gap-backfill', { method: 'POST', body: '{}' });
        const id = result.job?.id ?? result.job_id;
        return `Preenchimento histórico enfileirado${id == null ? '' : ` como job #${id}`}. O agente executará em segundo plano.`;
      }
      if (request.kind === 'recover') {
        const result = await api<{ message: string }>('/api/agent-recover', { method: 'POST' });
        return result.message;
      }
      if (request.kind === 'priority') {
        await api('/api/agent-priority', { method: 'PUT', body: JSON.stringify({ priority: request.value }) });
        return request.value === 'automatic' ? 'Modo automático salvo; a prioridade manual continua guardada.' : 'Modo manual salvo; a preferência escolhida vale para os próximos lotes.';
      }
      if (request.kind === 'manual-priority') {
        await api('/api/agent-manual-priority', { method: 'PUT', body: JSON.stringify({ priority: request.value }) });
        return request.value === 'candidates_first' ? 'Preferência salva: candidatos vinculados ao TSE primeiro.' : 'Preferência salva: demais parlamentares primeiro.';
      }
      if (request.kind === 'job-priority') {
        await api('/api/agent-job-priority', { method: 'PUT', body: JSON.stringify({ job_id: request.value || null }) });
        return request.value ? `Job #${request.value} priorizado para a próxima seleção, sem interromper o job em execução.` : 'Prioridade manual de job removida; a fila seguirá a ordem automática.';
      }
      if (request.kind === 'retry') {
        const result = await api<{ retry_seconds: number }>('/api/agent-retry-policy', { method: 'PUT', body: JSON.stringify({ retry_seconds: request.value }) });
        return `Nova tentativa configurada para ${result.retry_seconds} segundos após uma falha.`;
      }
      throw new Error('Ação de controle não reconhecida.');
    },
    onSuccess: (message) => {
      setFeedback(message);
      void client.invalidateQueries({ queryKey: ['agent-control'] });
      void client.invalidateQueries({ queryKey: ['dashboard-activity'] });
      void client.invalidateQueries({ queryKey: ['dashboard-overview'] });
    },
  });

  const disabled = !control?.writes_enabled || loading || change.isPending || Boolean(error);
  const activeJobs = control?.priority_jobs ?? [];
  const selectedPriority = String(control?.manual_job_priority ?? (control?.job?.status === 'running' ? control.job.id : ''));
  const selectedJob = activeJobs.some((job) => String(job.id) === selectedPriority) ? selectedPriority : '';
  const retryOptions = [10, 30, 60, 120, 300, 900, 1800, 3600];
  const retryValue = String(control?.retry_seconds ?? 60);
  const errorMessage = error || (change.error instanceof Error ? change.error.message : '');

  return <div className="agent-controls-body">
    <div className="agent-command-groups">
      <section className="agent-command-group agent-command-group-actions" aria-labelledby="agent-actions-title">
        <h3 id="agent-actions-title">Execução</h3>
        <p>Inicie, pause ou recupere uma coleta.</p>
        <div className="agent-action-buttons">
          <Button variant="primary" type="button" disabled={disabled} onClick={() => { setFeedback(''); change.mutate({ kind: 'execution', action: 'start' }); }}>{change.isPending ? 'Enviando solicitação…' : 'Iniciar / processar agora'}</Button>
          <Button type="button" disabled={disabled} onClick={() => { setFeedback(''); change.mutate({ kind: 'backfill' }); }}>Preencher dados históricos ausentes</Button>
          <Button type="button" disabled={disabled} onClick={() => { setFeedback(''); change.mutate({ kind: 'execution', action: control?.paused ? 'resume' : 'pause' }); }}>{control?.paused ? 'Retomar agente' : 'Pausar após as consultas atuais'}</Button>
          <Button type="button" disabled={disabled} onClick={() => { setFeedback(''); change.mutate({ kind: 'recover' }); }}>Recuperar job parado</Button>
        </div>
      </section>

      <section className="agent-command-group agent-command-group-priority" aria-labelledby="agent-priority-title">
        <h3 id="agent-priority-title">Prioridade da fila</h3>
        <p>Defina como os próximos lotes serão ordenados.</p>
        <div className="agent-command-fields">
          <SelectField label="Modo" labelClassName="agent-control-field" value={control?.priority ?? 'automatic'} disabled={disabled} onValueChange={(value) => { setFeedback(''); change.mutate({ kind: 'priority', value: value as 'automatic' | 'manual' }); }}>
            <option value="automatic">Automático · segue a fila</option>
            <option value="manual">Manual · usa a prioridade escolhida</option>
          </SelectField>
          <div className="agent-field-with-help">
            <SelectField label="Preferência manual" labelClassName="agent-control-field" value={control?.manual_priority ?? 'candidates_first'} disabled={disabled} onValueChange={(value) => { setFeedback(''); change.mutate({ kind: 'manual-priority', value: value as 'candidates_first' | 'remaining_first' }); }}>
              <option value="candidates_first">Candidatos vinculados ao TSE primeiro</option>
              <option value="remaining_first">Demais parlamentares primeiro</option>
            </SelectField>
            <small>Fica salva mesmo no modo automático.</small>
          </div>
          <div className="agent-field-with-help">
            <SelectField label="Job prioritário manual" labelClassName="agent-control-field" value={selectedJob} disabled={disabled} onValueChange={(value) => { setFeedback(''); change.mutate({ kind: 'job-priority', value }); }}>
              <option value="">Automático · próximo job da fila</option>
              {activeJobs.map((job) => <option key={job.id} value={String(job.id)}>Job #{job.id} · {job.status === 'running' ? 'em execução' : 'na fila'} · {job.kind} · {job.year ?? '—'}</option>)}
            </SelectField>
            <small>{activeJobs.length ? 'O job em execução vem pré-selecionado. A escolha afeta a próxima seleção e não interrompe um job ativo.' : 'Não há jobs ativos ou enfileirados para priorizar.'}</small>
          </div>
        </div>
      </section>

      <section className="agent-command-group agent-command-group-retry" aria-labelledby="agent-retry-title">
        <h3 id="agent-retry-title">Tentativas</h3>
        <p>Intervalo antes de repetir uma consulta que falhou.</p>
        <SelectField label="Aguardar antes de tentar novamente" labelClassName="agent-control-field" value={retryValue} disabled={disabled} onValueChange={(value) => { setFeedback(''); change.mutate({ kind: 'retry', value: Number(value) }); }}>
          {!retryOptions.includes(Number(retryValue)) && <option value={retryValue}>{retryValue} segundos · configuração atual</option>}
          {retryOptions.map((seconds) => <option key={seconds} value={seconds}>{seconds < 60 ? `${seconds} segundos` : seconds % 60 === 0 ? `${seconds / 60} minuto${seconds === 60 ? '' : 's'}` : `${seconds} segundos`}</option>)}
        </SelectField>
      </section>
    </div>
    <div className="agent-control-feedback" role={errorMessage ? 'alert' : 'status'} aria-live={errorMessage ? 'assertive' : 'polite'}>
      {errorMessage ? errorMessage : control?.writes_enabled === false ? 'Operação Node desabilitada na configuração deste servidor.' : feedback || 'Solicitação registrada; o agente verificará a fila em instantes.'}
    </div>
  </div>;
}

function terminalCommandState(activity: DashboardActivity | undefined, loading: boolean, error: Error | null): string {
  if (loading) return 'consultando o estado das buscas…';
  if (error) return 'estado das buscas indisponível';

  const job = activity?.job;
  const runtime = activity?.agent_runtime;
  if (!job) return runtime?.online ? 'agente ativo · aguardando próxima busca' : 'nenhuma busca em andamento · agente offline';

  const identity = `job #${job.id} · ${job.kind}${job.year ? ` · ${job.year}` : ''}`;
  const update = job.progress_data?.message ?? activity?.logs[0]?.message;
  const detail = update?.replace(/\s+/g, ' ').trim().slice(0, 110);

  switch (job.status) {
    case 'running': return `acompanhando ${identity}${detail ? ` · ${detail}` : ''}`;
    case 'queued': return `${identity} · aguardando execução`;
    case 'completed': return `último ${identity} · concluído`;
    case 'failed': return `último ${identity} · falhou`;
    case 'cancelled': return `último ${identity} · cancelado`;
    default: return `${identity} · ${job.status}`;
  }
}

function DashboardActivityPanel({ metrics }: { metrics: NodeDashboardMetrics | undefined }) {
  const activity = useApiQuery<DashboardActivity>(['dashboard-activity'], '/api/dashboard/activity', true, { staleTime: 0, refetchInterval: 5_000 });
  const job = activity.data?.job;
  const runtime = activity.data?.agent_runtime;
  const logs = activity.data?.logs ?? [];
  const status = runtime?.online ? 'Agente ativo' : job?.status === 'running' ? 'Job em execução' : job?.status === 'queued' ? 'Job na fila' : 'Sem atividade';
  const commandState = terminalCommandState(activity.data, activity.isLoading, activity.error instanceof Error ? activity.error : null);
  const footerDescription = activity.isLoading
    ? 'Consultando o estado do agente e os logs mais recentes…'
    : activity.error
      ? 'Não foi possível atualizar o estado das buscas agora.'
      : 'Estado das buscas atualizado automaticamente a cada 5 segundos.';
  return <div className="dashboard-terminal-window">
    <header className="dashboard-terminal-window-header"><div className="dashboard-terminal-traffic" aria-hidden="true"><i /><i /><i /></div><strong>Terminal · atividade da busca</strong><span className={`dashboard-terminal-live${runtime?.online || job?.status === 'running' ? ' is-live' : ''}`}>{status}</span></header>
    <div className="dashboard-terminal-window-body">
      <div className="dashboard-terminal-metadata">
        <span><small>FONTES</small><strong>{metrics?.sources.filter((source) => source.enabled).map((source) => source.id.toUpperCase()).slice(0, 5).join(' · ') || 'Fontes oficiais configuradas'}</strong></span>
        <span><small>ROTINA</small><strong>{job?.kind ?? '—'}</strong></span>
        <span><small>EXECUÇÃO</small><strong>{job ? `#${job.id} · ${status.toUpperCase()}` : 'Sem job registrado'}</strong></span>
        <span><small>ATIVIDADE</small><strong>{job ? `Ano ${job.year ?? '—'} · ${job.progress_data?.message ?? 'aguardando evento do worker'}` : 'Sem atividade recente'}</strong></span>
      </div>
      <div className="dashboard-terminal-log" role="log" aria-live="polite" aria-relevant="additions text">{activity.isLoading ? <p className="empty-inline">Carregando eventos do job…</p> : activity.error ? <p className="notice error">{activity.error.message}</p> : logs.length ? logs.map((log) => { const warning = log.level === 'warn' || log.level === 'error' || /\b(erro|falha|failed)\b/i.test(log.message); return <article key={log.id}><time>{new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(log.created_at))}</time><span className={warning ? 'dashboard-log-warning' : ''}>{warning ? 'AVISO' : 'INFO'}</span><p>{log.message}</p></article>; }) : <p className="empty-inline">Ainda não há eventos registrados para este job.</p>}</div>
      <footer><strong><span>PS C:\Vitrine\Agente&gt;</span> {commandState}</strong><small>{footerDescription}</small></footer>
    </div>
    {job?.error && <p className="notice error">{job.error}</p>}
  </div>;
}

function OperationalHeaderActions({ metrics, loading }: { metrics: NodeDashboardMetrics | undefined; loading: boolean }) {
  const client = useQueryClient();
  const exportDiagnostic = () => {
    if (!metrics) return;
    const report = {
      generated_at: metrics.generated_at,
      executor: metrics.executor,
      current_job: metrics.current_job ? {
        id: metrics.current_job.id, kind: metrics.current_job.kind, year: metrics.current_job.year,
        status: metrics.current_job.status, percent: metrics.current_job.percent,
        created_at: metrics.current_job.created_at, updated_at: metrics.current_job.updated_at,
      } : null,
      processing: metrics.processing,
      operation_progress: metrics.operation_progress,
      limitations: metrics.limitations,
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `vitrine-diagnostico-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const updated = metrics?.generated_at
    ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(new Date(metrics.generated_at))
    : '—';
  return <div className="dashboard-operational-actions"><span>Métricas atualizadas às {updated}</span><Button type="button" disabled={loading} onClick={() => { void client.invalidateQueries({ queryKey: ['node-dashboard-metrics'] }); }}>Atualizar métricas</Button><Button type="button" disabled={!metrics} onClick={exportDiagnostic}>Exportar diagnóstico</Button></div>;
}

function AgentProcessingSettingsPanel() {
  const client = useQueryClient();
  const settings = useApiQuery<AgentSettings>(['agent-settings'], '/api/agent-settings');
  const [level, setLevel] = useState(2);
  const [feedback, setFeedback] = useState('');
  const saveTimer = useRef<number | null>(null);
  const save = useMutation({ mutationFn: (next: number) => api('/api/agent-settings', { method: 'PUT', body: JSON.stringify({ level: next }) }), onSuccess: () => { setFeedback('Preferência salva no banco e definida como ritmo principal do agente.'); void client.invalidateQueries({ queryKey: ['agent-settings'] }); } });
  useEffect(() => { if (settings.data?.level) setLevel(settings.data.level); }, [settings.data?.level]);
  useEffect(() => () => { if (saveTimer.current !== null) window.clearTimeout(saveTimer.current); }, []);
  const selected = agentLevels[level] ?? agentLevels[2]!;
  const changeLevel = (next: number) => {
    setLevel(next);
    setFeedback('Salvando preferência…');
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => save.mutate(next), 450);
  };
  return settings.isLoading ? <Loading label="Carregando o ritmo salvo…" /> : settings.error ? <p className="notice error">{settings.error.message}</p> : <div className="agent-processing-control">
    <div className="agent-processing-label"><label htmlFor="agent-processing-level">Ritmo</label><output htmlFor="agent-processing-level">{selected.label}</output></div>
    <input id="agent-processing-level" type="range" min="1" max="5" step="1" value={level} disabled={settings.data?.writes_enabled === false} onChange={(event) => changeLevel(Number(event.target.value))} aria-valuetext={`${selected.label}; lotes de ${selected.batch_size} candidatos; ${selected.workers} em paralelo; até ${selected.requests_per_second} consultas por segundo`} />
    <p>Lotes de {number(selected.batch_size)} candidatos · {selected.workers} em paralelo · até {selected.requests_per_second} consultas/s</p>
    {settings.data?.level === null && <small>Ritmo personalizado em uso: {settings.data.workers} em paralelo e até {settings.data.requests_per_second} consultas/s. Salvar aplicará o nível escolhido acima.</small>}
    <small>{feedback || (settings.data?.updated_at ? `Configuração salva no banco · atualizado em ${date(settings.data.updated_at)}` : 'Configuração padrão do projeto.')}{save.error instanceof Error ? ` · ${save.error.message}` : ''}</small>
  </div>;
}

function ElectionSourceSettingsPanel({ enabled, settings, loading, error }: { enabled: boolean; settings: ApurationSettings | undefined; loading: boolean; error: Error | null }) {
  const client = useQueryClient();
  const [mode, setMode] = useState<ApurationSettings['mode']>('both');
  const [feedback, setFeedback] = useState('');
  const save = useMutation({ mutationFn: (next: ApurationSettings['mode']) => api('/api/election-apuration-settings', { method: 'PUT', body: JSON.stringify({ mode: next }) }), onSuccess: () => { setFeedback('Origem salva. As próximas consultas públicas já usarão essa preferência.'); void client.invalidateQueries({ queryKey: ['election-apuration-settings'] }); void client.invalidateQueries({ queryKey: ['apuracao'] }); void client.invalidateQueries({ queryKey: ['apuracao-capital'] }); void client.invalidateQueries({ queryKey: ['apuracao-estados'] }); } });
  useEffect(() => { if (settings?.mode) setMode(settings.mode); }, [settings?.mode]);
  if (!enabled) return <p className="empty-inline">Recolhido. Expanda esta seção para carregar os detalhes da apuração.</p>;
  return loading ? <Loading label="Carregando a configuração da apuração…" /> : error ? <p className="notice error">{error.message}</p> : settings ? <div className="election-source-settings-body" data-update-dirty={mode !== settings.mode || save.isPending}>
    <SelectField id="election-source-mode" label="Origem das consultas públicas" value={mode} onValueChange={(value) => setMode(value as ApurationSettings['mode'])}><option value="both">TSE + base · sincronizar a cada 2 horas</option><option value="tse">TSE · consulta direta</option><option value="database">Somente base local</option></SelectField>
    <div className="election-source-actions"><Button type="button" variant="primary" onClick={() => { setFeedback(''); save.mutate(mode); }} disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar origem'}</Button><span className="small">{number(settings.locations)} local(is) e {number(settings.candidates)} candidatura(s) · atualização TSE a cada {Math.floor(settings.refresh_interval_seconds / 3600)}h {Math.floor(settings.refresh_interval_seconds % 3600 / 60)}min no modo combinado.{feedback && <span role="status"> {' '}{feedback}</span>}</span></div>
  </div> : <p className="empty-inline">Sem configuração disponível.</p>;
}

function StateDashboard({ data, control, onControl, layout, ready, loadError, saveMessage, onLayoutChange }: {
  data: StateData;
  control?: { requested: boolean; configured: boolean; running: boolean; message: string } | undefined;
  onControl: (requested: boolean) => void;
  layout: DashboardSectionLayout;
  ready: boolean;
  loadError: boolean;
  saveMessage: string;
  onLayoutChange: (tab: 'federal' | 'state', layout: DashboardSectionLayout) => void;
}) {
  const profiles = data.profiles ?? [];
  const sections: DashboardSectionDefinition[] = [
    {
      id: 'state-coverage-summary', title: 'Cobertura estadual · Paraná e Santa Catarina',
      description: 'Candidaturas, bens e registros parlamentares estaduais disponíveis para o piloto.',
      content: <div className="dashboard-metrics">
        <article className="dashboard-metric"><span>Candidaturas estaduais 2026</span><strong>{number(data.candidates)}</strong><small>TSE · PR e SC</small></article>
        <article className="dashboard-metric"><span>Bens declarados</span><strong>{number(data.assets)}</strong><small>TSE · sem confundir com despesa parlamentar</small></article>
        <article className="dashboard-metric"><span>Perfis legislativos estaduais</span><strong>{number(profiles.reduce((sum, row) => sum + row.total, 0))}</strong><small>ALEP e Alesc</small></article>
        <article className="dashboard-metric"><span>Registros oficiais estaduais</span><strong>{number(data.official_records)}</strong><small>Dados já persistidos</small></article>
      </div>,
    },
    {
      id: 'state-agent-status', title: 'Agente Deputados Estaduais',
      description: control?.message ?? 'O coletor estadual ainda não está configurado.',
      status: <span className={`status-pill ${control?.running ? 'is-running' : ''}`}>{control?.running ? 'Em execução' : 'Parado'}</span>,
      content: <div className="agent-control-actions"><p className="small">Sua preferência de início fica salva no banco. O botão não inicia um processo enquanto o coletor não existir.</p><Button type="button" variant={control?.requested ? 'secondary' : 'primary'} onClick={() => onControl(!control?.requested)}>{control?.requested ? 'Manter desligado' : 'Solicitar início'}</Button>{control?.requested && <p className="notice">A preferência de início está registrada. O coletor ainda não foi configurado.</p>}</div>,
    },
  ];
  return <section className="dashboard-content">
    <DashboardLayout tab="state" sections={sections} layout={layout} ready={ready} loadError={loadError} saveMessage={saveMessage} onLayoutChange={onLayoutChange} />
    <DataNote>A aba estadual consulta apenas candidaturas, bens e registros estaduais de PR e SC, e não altera o site público.</DataNote>
  </section>;
}
